import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { Container } from "@/shared/components/layout/Container";
import { Section } from "@/shared/components/layout/Section";
import { Button } from "@/shared/components/ui/Button";
import { Logo } from "@/shared/components/ui/Logo";
import { FormField } from "@/shared/components/form/FormField";
import { PasswordFormField } from "@/shared/components/form/PasswordFormField";
import { useSignup } from "@/shared/lib/auth";

export function Signup() {
  const navigate = useNavigate();
  const signup = useSignup();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");

  const confirmMismatch = confirmPassword.length > 0 && confirmPassword !== password;

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (password !== confirmPassword) return;
    signup.mutate(
      { email, password, name: name.trim() ? name.trim() : undefined },
      { onSuccess: () => navigate("/app") },
    );
  }

  return (
    <Section
      spacing="lg"
      className="min-h-screen flex flex-col items-center justify-center gap-8"
    >
      <Logo to="/" />
      <Container size="sm">
        <div className="glass-surface-strong rounded-2xl p-8 flex flex-col gap-6">
          <div className="flex flex-col gap-2 text-center">
            <h1 className="type-heading-md text-text">Create your account</h1>
            <p className="type-body-base text-text-muted">
              Start collecting addresses for your next event.
            </p>
          </div>
          <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
            <FormField
              label="Name"
              name="name"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <FormField
              label="Email"
              type="email"
              name="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <PasswordFormField
              label="Password"
              name="password"
              autoComplete="new-password"
              required
              minLength={8}
              hint="At least 8 characters."
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <PasswordFormField
              label="Confirm password"
              name="confirmPassword"
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={confirmMismatch ? "Passwords don't match." : undefined}
            />
            {signup.error ? (
              <p className="type-body-sm text-danger-strong mx-4">
                {signup.error.message}
              </p>
            ) : null}
            <Button type="submit" disabled={signup.isPending}>
              {signup.isPending ? "Creating account…" : "Create account"}
            </Button>
          </form>
          <p className="type-body-sm text-text-muted text-center">
            Already have an account?{" "}
            <Link to="/auth/login" className="text-brand underline-offset-4 hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </Container>
    </Section>
  );
}
